import React from "react";
import {
  X,
  FileText,
  HelpCircle,
  Target,
  Users,
  Clock,
  Lightbulb,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface AdditionalMaterial {
  ek_id: number;
  icerik_json: {
    title: string;
    description?: string | null;
    hooks?: Array<{
      title: string;
      description: string;
      expected_outcome: string;
      materials_needed: string;
    }>;
    misconceptions?: Array<{
      misconception: string;
      clarification: string;
      why_misconception_occurs: string;
      fix_strategies: string;
      real_classroom_example: string;
    }>;
  };
  toolType: string;
}

interface CitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  material: AdditionalMaterial | null;
  description: string;
}

const CitationModal: React.FC<CitationModalProps> = ({
  isOpen,
  onClose,
  material,
  description,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !material) return null;

  const renderHooks = () => {
    if (!material.icerik_json.hooks) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("tedPlan.modal.lessonHooks")}
          </h3>
        </div>

        {material.icerik_json.hooks.map((hook, index) => (
          <div
            key={index}
            className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 space-y-4"
          >
            <Markdown remarkPlugins={[remarkGfm]}>{hook.title}</Markdown>

            <div className="prose prose-sm max-w-none dark:prose-invert">
              <Markdown remarkPlugins={[remarkGfm]}>
                {hook.description}
              </Markdown>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("tedPlan.modal.expectedOutcome")}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 pl-6 prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {hook.expected_outcome}
                  </Markdown>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("tedPlan.modal.materialsNeeded")}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 pl-6 prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {hook.materials_needed}
                  </Markdown>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMisconceptions = () => {
    if (!material.icerik_json.misconceptions) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("tedPlan.modal.commonMisconceptions")}
          </h3>
        </div>

        {material.icerik_json.misconceptions.map((misconception, index) => (
          <div
            key={index}
            className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-6 space-y-4"
          >
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-2">
                  {t("tedPlan.modal.misconception")}
                </h4>
                <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {misconception.misconception}
                  </Markdown>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                  {t("tedPlan.modal.clarification")}
                </h4>
                <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {misconception.clarification}
                  </Markdown>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                  {t("tedPlan.modal.whyOccurs")}
                </h4>
                <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {misconception.why_misconception_occurs}
                  </Markdown>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2">
                  {t("tedPlan.modal.fixStrategies")}
                </h4>
                <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {misconception.fix_strategies}
                  </Markdown>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-md p-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-yellow-600" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("tedPlan.modal.classroomExample")}
                  </h4>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed prose prose-sm max-w-none dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {misconception.real_classroom_example}
                  </Markdown>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full dark:bg-blue-900/30 dark:text-blue-300">
                    EK-{material.ek_id}
                  </span>
                  <span
                    className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${
                      material.toolType === "LESSON_HOOK"
                        ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        : "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
                    }`}
                  >
                    {material.toolType === "LESSON_HOOK"
                      ? t("tedPlan.materialType.lessonHook")
                      : t("tedPlan.materialType.misconception")}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {material.toolType === "LESSON_HOOK" && renderHooks()}
            {material.toolType === "COMMON_MISCONCEPTION" &&
              renderMisconceptions()}
          </div>

          {/* Footer */}
          <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitationModal;
